import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import API from '../services/api';
import { useAuth } from '../context/AuthContext';
import { subscribeToPushNotifications } from '../serviceWorkerRegistration';

const NotificationManager = () => {
  const { user } = useAuth();
  const [notificationPermission, setNotificationPermission] = useState(Notification.permission);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    // Kiểm tra xem browser có hỗ trợ notifications không
    if (!('Notification' in window)) {
      console.log('Trình duyệt này không hỗ trợ thông báo.');
      return;
    }

    // Cập nhật trạng thái quyền thông báo
    setNotificationPermission(Notification.permission);

    // Đăng ký nhận thông báo nếu đã đăng nhập
    if (user && user.id) {
      registerForPushNotifications();
    }
  }, [user]);

  // Đăng ký nhận push notifications
  const registerForPushNotifications = async () => {
    try {
      // Kiểm tra xem service worker đã được đăng ký chưa
      if (!('serviceWorker' in navigator)) {
        console.log('Service Worker không được hỗ trợ trong trình duyệt này');
        return;
      }

      // Kiểm tra xem service worker đã sẵn sàng chưa
      let registration;
      try {
        registration = await navigator.serviceWorker.ready;
      } catch (error) {
        console.error('Service Worker chưa sẵn sàng:', error);
        return;
      }

      // Đăng ký nhận push notifications với xử lý đặc biệt cho iOS
      try {
        const newSubscription = await subscribeToPushNotifications(registration);

        if (newSubscription) {
          setSubscription(newSubscription);

          // Liên kết subscription với user ID
          await linkSubscriptionToUser(newSubscription);
        }
      } catch (subscriptionError) {
        console.error('Lỗi khi đăng ký nhận push notifications:', subscriptionError);

        // Xử lý đặc biệt cho iOS
        if (isIOSDevice()) {
          console.log('Đây là thiết bị iOS, thử lại với cấu hình đặc biệt...');
          await handleIOSPushRegistration(registration);
        }
      }
    } catch (error) {
      console.error('Lỗi khi đăng ký nhận thông báo đẩy:', error);
    }
  };

  // Kiểm tra xem có phải thiết bị iOS không
  const isIOSDevice = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  };

  // Xử lý đăng ký push notification đặc biệt cho iOS
  const handleIOSPushRegistration = async (registration) => {
    try {
      // Đợi một chút trước khi thử lại trên iOS
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Thử đăng ký lại với timeout ngắn hơn cho iOS
      const newSubscription = await Promise.race([
        subscribeToPushNotifications(registration),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('iOS push registration timeout')), 10000)
        )
      ]);

      if (newSubscription) {
        setSubscription(newSubscription);
        await linkSubscriptionToUser(newSubscription);
        console.log('Đăng ký push notification thành công trên iOS');
      }
    } catch (error) {
      console.error('Lỗi khi đăng ký push notification trên iOS:', error);
      toast.warning('Không thể đăng ký thông báo đẩy trên thiết bị này. Vui lòng thử lại sau.');
    }
  };


  const linkSubscriptionToUser = async (subscription) => {
    try {
      if (!user || !user.id) return;

      const response = await API.post('/push-notifications/link-user', {
        subscription: subscription,
        userId: user.id
      });

      if (response.data.success) {
        console.log('Đã liên kết subscription với user ID thành công');
      }
    } catch (error) {
      console.error('Lỗi khi liên kết subscription với user ID:', error);

      // Xử lý lỗi network đặc biệt cho iOS
      if (isIOSDevice() && error.message && error.message.includes('Network Error')) {
        console.log('Phát hiện lỗi network trên iOS, thử lại với cấu hình khác...');
        await retryLinkSubscriptionForIOS(subscription);
      }
    }
  };

  // Thử lại liên kết subscription cho iOS với cấu hình đặc biệt
  const retryLinkSubscriptionForIOS = async (subscription) => {
    try {
      // Thử với timeout ngắn hơn và retry
      const response = await API.post('/push-notifications/link-user', {
        subscription: subscription,
        userId: user.id
      }, {
        timeout: 15000, // 15 giây thay vì 30 giây
        retry: 2,
        retryDelay: 2000
      });

      if (response.data.success) {
        console.log('Đã liên kết subscription với user ID thành công (iOS retry)');
        toast.success('Đã đăng ký thông báo đẩy thành công!');
      }
    } catch (retryError) {
      console.error('Lỗi khi retry liên kết subscription trên iOS:', retryError);
      toast.error('Không thể đăng ký thông báo đẩy. Vui lòng kiểm tra kết nối mạng.');
    }
  };

  // Yêu cầu quyền thông báo
  const requestNotificationPermission = async () => {
    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      
      if (permission === 'granted') {
        toast.success('Đã cấp quyền thông báo!');
        registerForPushNotifications();
      } else {
        toast.warning('Bạn đã từ chối quyền thông báo. Một số tính năng có thể không hoạt động đầy đủ.');
      }
    } catch (error) {
      console.error('Lỗi khi yêu cầu quyền thông báo:', error);
      toast.error('Không thể yêu cầu quyền thông báo.');
    }
  };

  // Gửi thông báo test
  const sendTestNotification = async () => {
    try {
      if (notificationPermission !== 'granted') {
        toast.warning('Bạn cần cấp quyền thông báo trước.');
        return;
      }

      // Sử dụng API instance thay vì axios trực tiếp
      const response = await API.post('/push-notifications/test', {});

      if (response.data.success) {
        toast.info('Đã gửi thông báo test. Vui lòng kiểm tra.');
      }
    } catch (error) {
      console.error('Lỗi khi gửi thông báo test:', error);

      // Xử lý lỗi network đặc biệt cho iOS
      if (isIOSDevice() && error.message && error.message.includes('Network Error')) {
        console.log('Phát hiện lỗi network trên iOS khi gửi test notification');
        toast.error('Lỗi kết nối mạng trên iOS. Vui lòng kiểm tra kết nối và thử lại.');
      } else {
        toast.error('Không thể gửi thông báo test. Vui lòng thử lại.');
      }
    }
  };

  // Component không render UI
  return null;
};

export default NotificationManager;